import { useState } from "react";
import { news } from "../data/profileData";
import { pressCoverage } from "../data/mediaCoverage";
import ChapterMark from "../components/ChapterMark";
import { motion } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { Reveal, staggerItem } from "../components/Reveal";
import Seo from "../components/Seo";

export default function News() {
  const featured = news[0];
  const rest = news.slice(1);
  const [showAll, setShowAll] = useState(false);
  const previewCount = 24;
  const shown = showAll ? pressCoverage : pressCoverage.slice(0, previewCount);

  return (
    <div data-testid="news-page" className="bg-white">
      <Seo
        path="/news"
        title="In The Press"
        description="Dr. P. B. Boss in the press — featured across 300+ publications worldwide, including his appointment as Honorary Tourism Commissioner, India to the European Union."
      />
      {/* TITLE PAGE */}
      <section
        data-testid="news-hero"
        className="pt-44 pb-24 lg:pt-52 lg:pb-32 bg-white relative"
      >
        <span className="hidden lg:block absolute left-7 top-1/2 -translate-y-1/2 v-label">
          P · B · Boss — Press
        </span>
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20 text-center">
          <ChapterMark numeral="III" label="In The Press" />
          <h1 className="mt-14 font-couture text-[clamp(3.5rem,9vw,8rem)] leading-[0.94] tracking-[-0.025em] text-[var(--ink)] reveal">
            Featured across
            <br />
            <span className="font-couture-italic text-[var(--gold-deep)]">
              India & the world.
            </span>
          </h1>
        </div>
      </section>

      {/* FEATURED */}
      {featured && (
        <section
          data-testid="news-featured"
          className="py-24 lg:py-32 bg-[var(--bg-soft)]"
        >
          <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <Reveal className="lg:col-span-7">
              <div className="img-frame overflow-hidden aspect-[16/11]">
                <img
                  src={featured.image}
                  alt={featured.title}
                  className="w-full h-full object-cover img-soft"
                />
              </div>
            </Reveal>
            <Reveal delay={0.15} className="lg:col-span-5 lg:pt-6">
              <p className="font-marcellus uppercase tracking-[0.42em] text-[0.66rem] text-[var(--gold-deep)] mb-7">
                §  Featured story
              </p>
              <p className="font-marcellus uppercase tracking-[0.34em] text-[0.72rem] text-[var(--ink-muted)] mb-5">
                {featured.date}
              </p>
              <h2 className="font-couture text-4xl lg:text-5xl xl:text-6xl tracking-[-0.02em] text-[var(--ink)] leading-[1.04]">
                {featured.title}
              </h2>

              {featured.outlets?.length > 0 && (
                <div className="mt-14 pt-12 border-t border-[var(--gold-line)]">
                  <p className="font-marcellus uppercase tracking-[0.42em] text-[0.66rem] text-[var(--gold-deep)] mb-7">
                    As covered by
                  </p>
                  <div className="flex flex-wrap gap-2.5">
                    {featured.outlets.map((o, i) => (
                      <a
                        key={i}
                        data-testid={`outlet-${i}`}
                        href={o.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 border border-[var(--gold-line)] font-marcellus uppercase tracking-[0.16em] text-[0.66rem] text-[var(--ink)] hover:border-[var(--gold-deep)] hover:text-[var(--gold-deep)] transition-colors"
                        title={`Read on ${o.name}`}
                        aria-label={`Read coverage on ${o.name}`}
                      >
                        {o.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </Reveal>
          </div>
        </section>
      )}

      {/* MORE — chronological list */}
      <section data-testid="news-more" className="py-32 lg:py-44 bg-white">
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20">
          <div className="mb-16 max-w-2xl">
            <p className="font-marcellus uppercase tracking-[0.5em] text-[0.66rem] text-[var(--gold-deep)] mb-6">
              More coverage
            </p>
            <h2 className="font-couture text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1]">
              Stories,{" "}
              <span className="font-couture-italic text-[var(--gold-deep)]">
                features
              </span>{" "}
              & broadcasts.
            </h2>
          </div>
          <ul className="border-t border-[var(--gold-line)]">
            {rest.map((n, i) => (
              <motion.li
                key={i}
                data-testid={`news-${i}`}
                variants={staggerItem}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                className="group grid grid-cols-12 gap-6 py-9 border-b border-[var(--gold-line)] items-center hover:bg-[var(--bg-soft)] transition-colors"
              >
                <span className="col-span-12 lg:col-span-1 numeral text-2xl text-[var(--gold-deep)]">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="col-span-6 lg:col-span-2 font-marcellus uppercase tracking-[0.32em] text-[0.7rem] text-[var(--ink-muted)]">
                  {n.date}
                </span>
                <h3
                  className={`col-span-12 font-couture text-2xl lg:text-3xl text-[var(--ink)] leading-snug tracking-[-0.01em] ${
                    n.image ? "lg:col-span-7" : "lg:col-span-9"
                  }`}
                >
                  {n.title}
                </h3>
                {n.image && (
                  <div className="hidden lg:block col-span-2">
                    <div className="img-frame aspect-[4/3] overflow-hidden">
                      <img
                        src={n.image}
                        alt={n.title}
                        className="w-full h-full object-cover img-soft"
                        loading="lazy"
                      />
                    </div>
                  </div>
                )}
              </motion.li>
            ))}
          </ul>
        </div>
      </section>

      {/* COMPLETE PRESS INDEX — 300+ syndicated features, each a backlink */}
      <section
        data-testid="press-index"
        className="py-32 lg:py-44 bg-[var(--bg-soft)]"
      >
        <div className="max-w-[1640px] mx-auto px-8 sm:px-14 lg:px-20">
          <Reveal className="text-center mb-16">
            <p className="font-marcellus uppercase tracking-[0.5em] text-[0.66rem] text-[var(--gold-deep)] mb-6">
              The Press Index
            </p>
            <h2 className="font-couture text-4xl sm:text-5xl lg:text-6xl tracking-[-0.02em] leading-[1]">
              Featured across{" "}
              <span className="font-couture-italic text-[var(--gold-deep)]">
                {pressCoverage.length}+ publications
              </span>
              <br />
              worldwide.
            </h2>
            <p className="mt-8 max-w-[40rem] mx-auto font-couture-italic text-lg lg:text-xl text-[var(--ink-soft)] leading-[1.55]">
              The appointment as Honorary Tourism Commissioner — India to the
              European Union — was carried by news wires and publications across
              India, Europe, the Gulf and the Americas. Each title below links
              to its original report.
            </p>
          </Reveal>

          <motion.div
            data-testid="press-index-grid"
            layout
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-10 gap-y-1 border-t border-[var(--gold-line)]"
          >
            {shown.map((p, i) => (
              <a
                key={p.url + i}
                data-testid={`press-link-${i}`}
                href={p.url}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-baseline gap-3 py-3.5 border-b border-[var(--gold-line-soft)] hover:bg-white transition-colors px-2 -mx-2"
                title={`Read on ${p.name}`}
              >
                <span className="numeral text-xs text-[var(--gold-deep)] w-8 shrink-0 tabular-nums">
                  {String(i + 1).padStart(3, "0")}
                </span>
                <span className="font-couture-italic text-lg text-[var(--ink)] leading-snug group-hover:text-[var(--gold-deep)] transition-colors">
                  {p.name}
                </span>
                <ArrowUpRight
                  size={13}
                  className="ml-auto mt-1 shrink-0 text-[var(--gold)] opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                />
              </a>
            ))}
          </motion.div>

          <div className="mt-16 text-center">
            <button
              data-testid="press-index-toggle"
              onClick={() => setShowAll((v) => !v)}
              className="btn-couture mx-auto"
            >
              {showAll
                ? "Show fewer"
                : `View all ${pressCoverage.length} features`}
              <ChevronDown
                size={14}
                className={`transition-transform duration-500 ${
                  showAll ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
